// Lenis smooth-scroll integration.
// Loaded dynamically from a CDN (no bundle bloat) and bridged with
// ScrollTrigger so pinned animations and scrubs stay in sync.
//
// Disabled automatically under prefers-reduced-motion — the user's preference
// should always win over our smooth-scroll preference.

import { prefersReducedMotion } from './trigger.js';

let lenisInstance = null;
let initPromise = null;

/**
 * Initialize Lenis once. Safe to call from multiple entry points.
 * Returns a promise that resolves to the Lenis instance (or null if disabled).
 */
export function initLenis(opts = {}) {
  if (initPromise) return initPromise;

  if (prefersReducedMotion()) {
    return Promise.resolve(null);
  }

  // Skip on mobile — native touch scroll is already smooth there, and adding
  // Lenis causes pinned ScrollTrigger animations (e.g. platform Lottie scrub)
  // to behave unpredictably on touch devices.
  if (window.innerWidth < 992) {
    return Promise.resolve(null);
  }

  initPromise = (async () => {
    try {
      const { default: Lenis } = await import('https://cdn.jsdelivr.net/npm/lenis@1.1.20/+esm');

      lenisInstance = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // expo-out
        smoothWheel: true,
        smoothTouch: false, // touch should feel native, not smoothed
        ...opts,
      });

      // Expose the live instance on window so per-page bundles (each compiled
      // with their own copy of this module) can reach the ONE real instance —
      // e.g. to stop/start scroll during a hero intro.
      if (typeof window !== 'undefined') window.__voyLenis = lenisInstance;

      // Bridge to ScrollTrigger: every Lenis scroll tick triggers a refresh
      // of ScrollTrigger's scroll calculations.
      if (typeof ScrollTrigger !== 'undefined') {
        lenisInstance.on('scroll', ScrollTrigger.update);
      }

      // Drive Lenis from GSAP's ticker so all scroll-driven motion shares
      // a single requestAnimationFrame loop. lagSmoothing(0) prevents GSAP
      // from fast-forwarding after a tab is backgrounded.
      gsap.ticker.add((time) => lenisInstance.raf(time * 1000));
      gsap.ticker.lagSmoothing(0);

      // Re-measure the scroll limit when the page grows after init.
      // The site CSS pins <html> to viewport height, so Lenis's built-in
      // autoResize (which observes documentElement) never fires when content
      // grows the page — leaving the scroll limit short and the page bottom
      // unreachable on long CMS articles (lazy images, late form/swiper
      // embeds, web-font reflow). <body> DOES grow with content, so observe
      // it and re-measure only when its height actually changes (avoids
      // thrash from scroll-driven animations that don't affect page height).
      let lastHeight = document.body.scrollHeight;
      let resizeRaf = 0;
      const resyncLenis = () => {
        resizeRaf = 0;
        const height = document.body.scrollHeight;
        if (height !== lastHeight) {
          lastHeight = height;
          lenisInstance.resize();
        }
      };
      const scheduleResync = () => {
        if (!resizeRaf) resizeRaf = requestAnimationFrame(resyncLenis);
      };
      if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(scheduleResync).observe(document.body);
      }
      // Immediate belt-and-suspenders triggers for content that settles late.
      window.addEventListener('load', () => lenisInstance.resize());
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => lenisInstance.resize());
      }

      return lenisInstance;
    } catch (err) {
      console.warn('[motion] Lenis failed to load — falling back to native scroll', err);
      return null;
    }
  })();

  return initPromise;
}

/** Get the live Lenis instance, if initialized. */
export function getLenis() {
  return lenisInstance;
}
